import _Object$keys from 'babel-runtime/core-js/object/keys';
import _JSON$stringify from 'babel-runtime/core-js/json/stringify';
import { sequenceSelectors } from '@rosetta/gaia-data';
import { getLogger, getReduxStore } from '../init';
import { progressModel } from './progress-model';

export function addStepAttemptToProgress(message, progress, assignedCourseInfo) {
  // make a deep copy of progress
  var newCourseProgress = JSON.parse(_JSON$stringify(progress));

  var sequence = sequenceSelectors.getSequence(getReduxStore().getState());
  var activity = sequence.activities.find(function (act) {
    return act.activityId === message.activityId;
  });
  var step = activity.steps.find(function (step) {
    return step.activityStepId === message.activityStepId;
  });
  var newStepHasSubsteps = progressModel.stepHasSubsteps(step);

  // check to see if we have anything in progress yet, empty progress
  //  looks like { courseId: some-guid } and that's it
  // TODO ********** make sure we need this test and creator because see progress-sagas.js line 72ish
  if (!progressModel.courseWasStarted(newCourseProgress)) {
    // this is the first progress in this course, we must build all the course props
    newCourseProgress.id = newCourseProgress.courseId;
    newCourseProgress.countOfSequencesInCourse = assignedCourseInfo.sequences.length;
    newCourseProgress.percentComplete = 0;
    newCourseProgress.sequences = [];
  }
  var sequenceProgress = progressModel.getSequenceProgress(newCourseProgress, message.sequenceId);
  if (!sequenceProgress) {
    sequenceProgress = {
      id: message.sequenceId,
      sequenceId: message.sequenceId,
      version: message.sequenceVersion,
      percentComplete: 0,
      bestGrade: null,
      countOfActivities: sequence.activities.length,
      activities: []
    };
    newCourseProgress.sequences.push(sequenceProgress);
  }
  var activityProgress = progressModel.getActivityProgress(sequenceProgress, message.activityId);
  if (!activityProgress) {
    activityProgress = {
      id: message.activityId,
      activityId: message.activityId,
      percentComplete: 0,
      attempts: []
    };
    sequenceProgress.activities.push(activityProgress);
  }
  var activityAttempt = activityProgress.attempts.find(function (attempt) {
    return attempt.activityAttemptId === message.activityAttemptId;
  });
  if (!activityAttempt) {
    activityAttempt = {
      id: message.activityAttemptId,
      activityAttemptId: message.activityAttemptId,
      steps: []
    };
    activityProgress.attempts.push(activityAttempt);
  }
  var stepProgressId = message.activityAttemptId + '|' + message.activityStepId;
  if (newStepHasSubsteps && Array.isArray(message.answers)) {
    // each new progress message will have only one substep answer in it
    var answerInfo = progressModel.parseSubstepAnswer(message.answers[0]);
    if (answerInfo) {
      stepProgressId += '|' + answerInfo.contentId;
    }
  }
  var stepProgress = activityAttempt.steps.find(function (stepProg) {
    return stepProg.id === stepProgressId;
  });
  if (!stepProgress) {
    stepProgress = {
      id: stepProgressId,
      activityStepId: message.activityStepId,
      attempts: []
    };
    activityAttempt.steps.unshift(stepProgress);
  }

  // **** now that we have all the supporting structures in place, add the step attempt
  //  and calculate the progress
  //
  //  the following bit is because the GPT rounds to 6 decimal points and we want to match it
  var SCORE_PRECISION = 1000000;
  var roundedScore = Math.round(message.score * SCORE_PRECISION) / SCORE_PRECISION;
  //
  var stepAttempt = {
    id: message.activityStepAttemptId,
    activityStepAttemptId: message.activityStepAttemptId,
    score: roundedScore,
    skipped: message.skip,
    endTimestamp: message.endTimestamp.toISOString(),
    answers: message.answers || []
  };
  stepProgress.attempts.push(stepAttempt);

  // *** activityProgress calculation:
  //
  if (activity.ordering === 'tree') {
    activityProgress.percentComplete = progressModel.getTreeActivityPercentComplete(activityProgress, activity);
    if (activityProgress.percentComplete === 1) {
      activityProgress.bestGrade = progressModel.getTreeActivityScore(activityProgress);
    } else {
      activityProgress.bestGrade = 0;
    }
  } else {
    // for each step in a non-tree activity:
    //  substeps are counted as if they were steps
    //  figure percentComplete, which is "percent of steps/substeps which were visited and not skipped"
    //  if every step was attempted then figure the bestGrade for the activity, else bestGrade is null
    //
    var allStepsCount = 0;
    var completedStepsCount = 0;
    var stepsScoresSum = 0;
    activity.steps.forEach(function (step) {
      var hasSubsteps = progressModel.stepHasSubsteps(step);

      if (hasSubsteps) {
        var substepCount = progressModel.getSubstepCount(step);
        allStepsCount += substepCount;
        var subStepInfos = progressModel.getSubstepsBestScores(activityProgress, step.activityStepId);
        subStepInfos.forEach(function (ssInfo) {
          stepsScoresSum += ssInfo.score;
          completedStepsCount++;
        });
      } else {
        allStepsCount++;
        if (progressModel.stepWithoutSubstepsIsComplete(activityProgress, step.activityStepId)) {
          stepsScoresSum += progressModel.getBestScoreForStepWithoutSubsteps(activityProgress, step.activityStepId);
          completedStepsCount++;
        }
      }
    });
    var newPercentComplete = completedStepsCount / allStepsCount;
    var oldPercentComplete = activityProgress.percentComplete || 0;
    activityProgress.percentComplete = Math.max(oldPercentComplete, newPercentComplete);
    if (activityProgress.percentComplete === 1) {
      var newGrade = stepsScoresSum / allStepsCount;
      var oldGrade = activityProgress.bestGrade || 0;
      activityProgress.bestGrade = Math.max(oldGrade, newGrade);
    } else {
      activityProgress.bestGrade = 0;
    }
  }

  // sequence progress
  if (sequenceProgress.countOfActivities > 0) {
    var countOfActivitiesDone = 0;
    sequenceProgress.activities.forEach(function (actProg) {
      if (progressModel.activityIsComplete(actProg)) {
        countOfActivitiesDone++;
      }
    });
    sequenceProgress.percentComplete = countOfActivitiesDone / sequenceProgress.countOfActivities;
    sequenceProgress.bestGrade = progressModel.sequenceScore(sequenceProgress);
  } else {
    // if we get here it means some content is broken
    sequenceProgress.percentComplete = 0;
    sequenceProgress.bestGrade = null;
  }

  // course progress percentComplete
  //  NOTE that this calculation will not always match the GPT percentComplete because
  //  the GPT filters out expert-graded activities from the denominator when it does the calc.
  //  We do not have the data to do that filtering because we have an assignedCourseInfo, which is
  //  very spare.  We only have detailed info (i.e. any info about the activities) for the current sequence
  //  so we can't report on the whole course.)  HOWEVER, those activities are obsolete and
  //  only exist for users who submitted them in the past, there should not be a lot
  //  of users for whom this (local) calculated value is wrong.
  //  ALSO, as far as I can tell, the only place courseProgress.percentComplete is currently used
  //   is in a Braze message.
  var countOfActivitiesInCourse = 0;
  assignedCourseInfo.sequences.forEach(function (seq) {
    countOfActivitiesInCourse += seq.numberOfActivities;
  });
  var countOfActivitiesDoneInCourse = 0;
  newCourseProgress.sequences.forEach(function (seqProg) {
    seqProg.activities.forEach(function (actProg) {
      if (progressModel.activityIsComplete(actProg)) {
        countOfActivitiesDoneInCourse++;
      }
    });
  });
  if (countOfActivitiesInCourse > 0) {
    newCourseProgress.percentComplete = countOfActivitiesDoneInCourse / countOfActivitiesInCourse;
  } else {
    // this is some funky content error
    newCourseProgress.percentComplete = 0;
  }

  return newCourseProgress;
}

// development util **********************************************************************************
export var compareProgressObjects = function compareProgressObjects(objA, objB) {
  if (!objA || !objB) {
    if (objA !== objB) {
      getLogger().warn('***PROG*** missing obj mismatch');
      return false;
    }
    return true;
  }

  var keysA = _Object$keys(objA).filter(function (key) {
    return key !== '__typename';
  });
  var keysB = _Object$keys(objB).filter(function (key) {
    return key !== '__typename';
  });
  if (keysA.length !== keysB.length) {
    getLogger().warn('***PROG*** keys length mismatch A "' + keysA.join() + '" B "' + keysB.join() + '"');
  }
  for (var i = 0; i < keysA.length; i++) {
    if (!keysB.includes(keysA[i])) {
      getLogger().warn('***PROG*** keys mismatch A "' + keysA.join() + '" B "' + keysB.join() + '"');
    }
  }

  for (var _i = 0; _i < keysA.length; _i++) {
    var propKey = keysA[_i];
    var propA = objA[propKey];
    var propB = objB[propKey];

    if (typeof propA !== typeof propB) {
      getLogger().warn('***PROG*** type mismatch key is ' + propKey + ' A is ' + typeof propA + ' B is ' + typeof propB);
    }
    if (Array.isArray(propA) !== Array.isArray(propB)) {
      getLogger().warn('array type mismatch A key is ' + propKey + ' is ' + Array.isArray(propA) + ' B is ' + Array.isArray(propB));
    }

    // progress can have arrays of objects but nothing else
    if (Array.isArray(propA)) {
      if (propA.length !== propB.length) {
        getLogger().warn('***PROG*** prop array length mismatch key is ' + propKey + ' A is ' + propA.length + ' B is ' + propB.length);
      }

      var _loop = function _loop(_i2) {
        var memberA = propA[_i2];
        var memberB = void 0;
        if (propKey === 'answers') {
          memberB = propB.find(function (memB) {
            return typeof memB === 'object' && memB.answer === memberA.answer;
          });
        } else {
          memberB = propB.find(function (memB) {
            return typeof memB === 'object' && memB.id === memberA.id;
          });
        }
        if (!compareProgressObjects(memberA, memberB)) {}
      };

      for (var _i2 = 0; _i2 < propA.length; _i2++) {
        _loop(_i2);
      }
    } else if (typeof propA === 'object' && propA !== null) {
      if (!compareProgressObjects(propA, propB)) {}
    } else if (propA !== propB) {
      // special case for date mismatch because server stamps after
      if (typeof propA === 'string' && !isNaN(Date.parse(propA))) {
        var dateA = new Date(propA);
        var dateB = new Date(propB);
        var msDiff = Math.abs(dateA.getTime() - dateB.getTime());
        if (msDiff > 2000) {
          getLogger().warn('***PROG*** date mismatch key is ' + propKey + ' A is ' + propA + ' B is ' + propB);
        }
      } else {
        getLogger().warn('***PROG*** value mismatch key is ' + propKey + ' A is ' + propA + ' B is ' + propB);
      }
    }
  }
  return true;
};